<script lang="ts">
import { computed, toRef } from 'vue';

import { useTabButton } from './composables/use-tab-button';

import type { PhxsgTabsLayout, PhxsgTabsPanelData } from './tabs.types';

export interface PhxsgTabButtonProps {
  instanceId: string;
  tab: PhxsgTabsPanelData;
  active: boolean;
  layout: PhxsgTabsLayout;
  classNames?: string[] | string;
  icon?: string;
  iconPath?: string;
  iconFirst?: boolean;
}
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<PhxsgTabButtonProps>(), {
  classNames: () => [],
  icon: '',
  iconPath: '',
  iconFirst: false,
});

const emit = defineEmits<{
  'tab-resize': [element: Element | null | undefined];
  'tab-active': [element: HTMLElement | null | undefined];
  'tab-click': [id: string, event: MouseEvent];
  mousedown: [];
}>();

const { buttonRef, onTabClick, onTabMouseDown } = useTabButton(
  { active: toRef(props, 'active'), id: props.tab.id },
  { emit },
);

const buttonClasses = computed(() => [
  'phxsg-tabs__button',
  props.classNames,
  {
    'phxsg-tabs__button--alternate': props.layout === 'start',
    'phxsg-tabs__button--expandable': props.layout === 'expandable',
    'phxsg-tabs__button--empty': props.tab.empty,
    'phxsg-is-active': props.active,
  },
]);
</script>

<template>
  <li
    :class="[
      'phxsg-tabs__item',
      {
        'phxsg-is-active': props.active,
      },
    ]"
    role="presentation"
  >
    <button
      :id="`${props.instanceId}-${props.tab.id}-tab-button`"
      ref="buttonRef"
      type="button"
      :aria-controls="`${props.instanceId}-tab-${props.tab.id}`"
      :aria-selected="`${props.active}`"
      :tabindex="props.active ? 0 : -1"
      role="tab"
      :class="buttonClasses"
      :disabled="props.tab.disabled"
      @click="onTabClick"
      @mousedown="onTabMouseDown"
    >
      <svg
        v-if="props.icon !== '' && props.iconPath !== '' && props.iconFirst"
        class="phxsg-tabs__button-icon"
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
      >
        <use :href="props.iconPath" :xlink:href="props.iconPath" />
      </svg>

      <template v-if="props.layout === 'expandable'">
        <span class="phxsg-tabs__button-short-label">{{ props.tab.shortLabel }}</span>
        <span class="phxsg-tabs__button-label" :style="{ width: props.active ? 'initial' : '0' }">
          {{ ` ${props.tab.label}` }}
        </span>
      </template>

      <span v-else class="phxsg-tabs__button-label">{{ props.tab.label }}</span>

      <svg
        v-if="props.icon !== '' && props.iconPath !== '' && !props.iconFirst"
        class="phxsg-tabs__button-icon"
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
      >
        <use :href="props.iconPath" :xlink:href="props.iconPath" />
      </svg>

      <span v-if="props.tab.badge !== ''" class="phxsg-tabs__button-badge">{{ props.tab.badge }}</span>
    </button>
  </li>
</template>
